import React, { useState } from 'react';
import { AlertCircleIcon, BellIcon, CalendarIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, FileIcon, SettingsIcon, ShieldIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
export function ReminderSettings({
  vendor
}) {
  // State for notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  // State for reminder timeframes
  const [reminderSettings, setReminderSettings] = useState({
    documents: {
      firstReminder: 30,
      secondReminder: 14,
      finalReminder: 7
    },
    invoices: {
      firstReminder: 14,
      secondReminder: 7,
      finalReminder: 3
    },
    contracts: {
      firstReminder: 60,
      secondReminder: 30,
      finalReminder: 14
    }
  });
  // State for upcoming expirations
  const [upcomingExpirations, setUpcomingExpirations] = useState([{
    id: 1,
    type: 'Insurance',
    name: 'General Liability Insurance',
    expirationDate: '2023-12-30',
    daysRemaining: 60,
    status: 'Active'
  }, {
    id: 2,
    type: 'Insurance',
    name: 'Workers Compensation Insurance',
    expirationDate: '2023-11-15',
    daysRemaining: 15,
    status: 'Expiring Soon'
  }, {
    id: 3,
    type: 'Certification',
    name: 'Professional Certification',
    expirationDate: '2023-11-01',
    daysRemaining: 1,
    status: 'Critical'
  }, {
    id: 4,
    type: 'Contract',
    name: 'Service Level Agreement',
    expirationDate: '2024-03-15',
    daysRemaining: 135,
    status: 'Active'
  }, {
    id: 5,
    type: 'License',
    name: 'Business License',
    expirationDate: '2024-05-20',
    daysRemaining: 201,
    status: 'Active'
  }]);
  // Toggle sections
  const [expandedSections, setExpandedSections] = useState({
    notifications: true,
    reminders: true,
    upcoming: true
  });
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Handle reminder setting changes
  const handleReminderChange = (category, level, value) => {
    setReminderSettings({
      ...reminderSettings,
      [category]: {
        ...reminderSettings[category],
        [level]: parseInt(value)
      }
    });
  };
  // Format date for display
  const formatDate = dateString => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Get status color
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Expiring Soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'Critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-680" />;
      case 'Expiring Soon':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-681" />;
      case 'Critical':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-682" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-683" />;
    }
  };
  // Save notification preferences
  const saveNotificationPreferences = () => {
    // In a real app, this would send the preferences to the server
    alert('Notification preferences saved successfully!');
  };
  // Save reminder settings
  const saveReminderSettings = () => {
    // In a real app, this would send the settings to the server
    alert('Reminder settings saved successfully!');
  };
  return <div data-id="element-684">
      <div className="mb-8" data-id="element-685">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-686">
          Reminders & Notifications
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-687">
          Manage your notification preferences and view upcoming expirations
        </p>
      </div>
      {/* Notification Preferences */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-688">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('notifications')} data-id="element-689">
          <div className="flex items-center" data-id="element-690">
            <BellIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-691" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-692">
              Notification Preferences
            </h3>
          </div>
          {expandedSections.notifications ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-693" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-694" />}
        </div>
        {expandedSections.notifications && <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-695">
            <div className="space-y-6" data-id="element-696">
              <div className="flex items-center justify-between" data-id="element-697">
                <div data-id="element-698">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-699">
                    Email Notifications
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-700">
                    Receive notifications via email
                  </p>
                </div>
                <button type="button" onClick={() => setEmailNotifications(!emailNotifications)} className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" aria-pressed="false" aria-labelledby="email-notifications-label" data-id="element-701">
                  <span className="sr-only" data-id="element-702">Use setting</span>
                  <span aria-hidden="true" className={`${emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'} pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200 ${emailNotifications ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-703"></span>
                </button>
              </div>
              <div className="flex items-center justify-between" data-id="element-704">
                <div data-id="element-705">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-706">
                    SMS Notifications
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-707">
                    Receive notifications via text message
                  </p>
                </div>
                <button type="button" onClick={() => setSmsNotifications(!smsNotifications)} className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" aria-pressed="false" aria-labelledby="sms-notifications-label" data-id="element-708">
                  <span className="sr-only" data-id="element-709">Use setting</span>
                  <span aria-hidden="true" className={`${smsNotifications ? 'bg-indigo-600' : 'bg-gray-200'} pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200 ${smsNotifications ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-710"></span>
                </button>
              </div>
              <div className="flex items-center justify-between" data-id="element-711">
                <div data-id="element-712">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-713">
                    In-App Notifications
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-714">
                    Receive notifications in the vendor portal
                  </p>
                </div>
                <button type="button" onClick={() => setInAppNotifications(!inAppNotifications)} className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" aria-pressed="false" aria-labelledby="app-notifications-label" data-id="element-715">
                  <span className="sr-only" data-id="element-716">Use setting</span>
                  <span aria-hidden="true" className={`${inAppNotifications ? 'bg-indigo-600' : 'bg-gray-200'} pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200 ${inAppNotifications ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-717"></span>
                </button>
              </div>
              <div className="flex justify-end" data-id="element-718">
                <button type="button" onClick={saveNotificationPreferences} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-719">
                  Save Preferences
                </button>
              </div>
            </div>
          </div>}
      </div>
      {/* Reminder Settings */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-720">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('reminders')} data-id="element-721">
          <div className="flex items-center" data-id="element-722">
            <SettingsIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-723" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-724">
              Reminder Settings
            </h3>
          </div>
          {expandedSections.reminders ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-725" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-726" />}
        </div>
        {expandedSections.reminders && <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-727">
            <p className="text-sm text-gray-500 mb-4" data-id="element-728">
              Configure when you receive reminders before important dates
            </p>
            <div className="space-y-6" data-id="element-729">
              <div data-id="element-730">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-731">
                  Document Expiration Reminders
                </h4>
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-4" data-id="element-732">
                  <div data-id="element-733">
                    <label htmlFor="documents-first" className="block text-xs font-medium text-gray-700" data-id="element-734">
                      First Reminder (days before)
                    </label>
                    <select id="documents-first" value={reminderSettings.documents.firstReminder} onChange={e => handleReminderChange('documents', 'firstReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-735">
                      <option value="90" data-id="element-736">90 days</option>
                      <option value="60" data-id="element-737">60 days</option>
                      <option value="45" data-id="element-738">45 days</option>
                      <option value="30" data-id="element-739">30 days</option>
                      <option value="15" data-id="element-740">15 days</option>
                    </select>
                  </div>
                  <div data-id="element-741">
                    <label htmlFor="documents-second" className="block text-xs font-medium text-gray-700" data-id="element-742">
                      Second Reminder (days before)
                    </label>
                    <select id="documents-second" value={reminderSettings.documents.secondReminder} onChange={e => handleReminderChange('documents', 'secondReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-743">
                      <option value="30" data-id="element-744">30 days</option>
                      <option value="21" data-id="element-745">21 days</option>
                      <option value="14" data-id="element-746">14 days</option>
                      <option value="7" data-id="element-747">7 days</option>
                    </select>
                  </div>
                  <div data-id="element-748">
                    <label htmlFor="documents-final" className="block text-xs font-medium text-gray-700" data-id="element-749">
                      Final Reminder (days before)
                    </label>
                    <select id="documents-final" value={reminderSettings.documents.finalReminder} onChange={e => handleReminderChange('documents', 'finalReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-750">
                      <option value="14" data-id="element-751">14 days</option>
                      <option value="7" data-id="element-752">7 days</option>
                      <option value="3" data-id="element-753">3 days</option>
                      <option value="1" data-id="element-754">1 day</option>
                    </select>
                  </div>
                </div>
              </div>
              <div data-id="element-755">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-756">
                  Invoice Reminders
                </h4>
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-4" data-id="element-757">
                  <div data-id="element-758">
                    <label htmlFor="invoices-first" className="block text-xs font-medium text-gray-700" data-id="element-759">
                      First Reminder (days before)
                    </label>
                    <select id="invoices-first" value={reminderSettings.invoices.firstReminder} onChange={e => handleReminderChange('invoices', 'firstReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-760">
                      <option value="30" data-id="element-761">30 days</option>
                      <option value="21" data-id="element-762">21 days</option>
                      <option value="14" data-id="element-763">14 days</option>
                      <option value="7" data-id="element-764">7 days</option>
                    </select>
                  </div>
                  <div data-id="element-765">
                    <label htmlFor="invoices-second" className="block text-xs font-medium text-gray-700" data-id="element-766">
                      Second Reminder (days before)
                    </label>
                    <select id="invoices-second" value={reminderSettings.invoices.secondReminder} onChange={e => handleReminderChange('invoices', 'secondReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-767">
                      <option value="14" data-id="element-768">14 days</option>
                      <option value="7" data-id="element-769">7 days</option>
                      <option value="5" data-id="element-770">5 days</option>
                      <option value="3" data-id="element-771">3 days</option>
                    </select>
                  </div>
                  <div data-id="element-772">
                    <label htmlFor="invoices-final" className="block text-xs font-medium text-gray-700" data-id="element-773">
                      Final Reminder (days before)
                    </label>
                    <select id="invoices-final" value={reminderSettings.invoices.finalReminder} onChange={e => handleReminderChange('invoices', 'finalReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-774">
                      <option value="7" data-id="element-775">7 days</option>
                      <option value="3" data-id="element-776">3 days</option>
                      <option value="2" data-id="element-777">2 days</option>
                      <option value="1" data-id="element-778">1 day</option>
                    </select>
                  </div>
                </div>
              </div>
              <div data-id="element-779">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-780">
                  Contract Expiration Reminders
                </h4>
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-4" data-id="element-781">
                  <div data-id="element-782">
                    <label htmlFor="contracts-first" className="block text-xs font-medium text-gray-700" data-id="element-783">
                      First Reminder (days before)
                    </label>
                    <select id="contracts-first" value={reminderSettings.contracts.firstReminder} onChange={e => handleReminderChange('contracts', 'firstReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-784">
                      <option value="90" data-id="element-785">90 days</option>
                      <option value="60" data-id="element-786">60 days</option>
                      <option value="45" data-id="element-787">45 days</option>
                      <option value="30" data-id="element-788">30 days</option>
                    </select>
                  </div>
                  <div data-id="element-789">
                    <label htmlFor="contracts-second" className="block text-xs font-medium text-gray-700" data-id="element-790">
                      Second Reminder (days before)
                    </label>
                    <select id="contracts-second" value={reminderSettings.contracts.secondReminder} onChange={e => handleReminderChange('contracts', 'secondReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-791">
                      <option value="60" data-id="element-792">60 days</option>
                      <option value="45" data-id="element-793">45 days</option>
                      <option value="30" data-id="element-794">30 days</option>
                      <option value="15" data-id="element-795">15 days</option>
                    </select>
                  </div>
                  <div data-id="element-796">
                    <label htmlFor="contracts-final" className="block text-xs font-medium text-gray-700" data-id="element-797">
                      Final Reminder (days before)
                    </label>
                    <select id="contracts-final" value={reminderSettings.contracts.finalReminder} onChange={e => handleReminderChange('contracts', 'finalReminder', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-798">
                      <option value="30" data-id="element-799">30 days</option>
                      <option value="21" data-id="element-800">21 days</option>
                      <option value="14" data-id="element-801">14 days</option>
                      <option value="7" data-id="element-802">7 days</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-803">
                <button type="button" onClick={saveReminderSettings} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-804">
                  Save Settings
                </button>
              </div>
            </div>
          </div>}
      </div>
      {/* Upcoming Expirations */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-805">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('upcoming')} data-id="element-806">
          <div className="flex items-center" data-id="element-807">
            <CalendarIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-808" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-809">
              Upcoming Expirations & Renewals
            </h3>
          </div>
          {expandedSections.upcoming ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-810" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-811" />}
        </div>
        {expandedSections.upcoming && <div className="border-t border-gray-200" data-id="element-812">
            <ul className="divide-y divide-gray-200" data-id="element-813">
              {upcomingExpirations.map(item => <li key={item.id} className="px-4 py-4 sm:px-6" data-id="element-814">
                  <div className="flex items-center justify-between" data-id="element-815">
                    <div className="flex items-center" data-id="element-816">
                      {getStatusIcon(item.status)}
                      <p className="ml-2 text-sm font-medium text-indigo-600 truncate" data-id="element-817">
                        {item.name}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-818">
                      <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(item.status)}`} data-id="element-819">
                        {item.status}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-820">
                    <div className="sm:flex" data-id="element-821">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-822">
                        {item.type}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-823">
                      <div data-id="element-824">
                        <p data-id="element-825">Expires: {formatDate(item.expirationDate)}</p>
                        <p className={`text-xs mt-1 ${item.daysRemaining <= 7 ? 'text-red-600' : item.daysRemaining <= 30 ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-826">
                          {item.daysRemaining <= 0 ? 'Expired' : `${item.daysRemaining} days remaining`}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>}
      </div>
    </div>;
}